// One fetch of everything the console reads, plus a 60 s poll of the two live bits
// (/api/monitor + /api/dials) while the tab is visible.
import { useCallback, useEffect, useRef, useState } from 'react';
import { authFetch } from './api';
import type {
  ActionRow,
  PipelineKey,
  CalendlyRow,
  DialChange,
  DialPatch,
  Dials,
  DialsIntent,
  Health,
  MonitorResponse,
  ProbeRequest,
  ProbeResult,
  RegisteredWebhook,
  ReplayResponse,
  SecretStatus,
  Service,
  TtsRow,
  WebhooksResponse,
} from './types';

export const LEDGER_LIMIT = 200;
const POLL_MS = 60_000;

export interface ConsoleData {
  service: Service | null;
  webhooks: WebhooksResponse | null;
  dials: Dials | null;
  intent: Record<string, Record<string, boolean>> | null;
  /** who last changed each dial and when */
  dialChanges: Record<string, DialChange | null>;
  monitor: MonitorResponse | null;
  tts: TtsRow[];
  calendly: CalendlyRow[];
  secrets: SecretStatus[];
  actions: ActionRow[];
  health: Health | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  setDials: (patch: DialPatch) => Promise<void>;
  /** POST /api/ledger/tts/:key/replay — resolves with the outcome; the row is refreshed in place */
  replayTts: (key: string, dryRun: boolean) => Promise<ReplayResponse>;
  /** POST /api/tools/probe — the new row shows up in `tts` on the next reload (processing is async) */
  sendProbe: (req: ProbeRequest) => Promise<ProbeResult>;
  /** DELETE /api/webhooks/:id with the id typed back; refreshes the webhook list from the response */
  deleteWebhook: (id: string, confirm: string) => Promise<void>;
  /** POST /api/webhooks — the response holds the one-time secretKey; the list + secrets status are re-read */
  registerWebhook: (pipeline: PipelineKey) => Promise<RegisteredWebhook>;
  /** POST /api/webhooks/:id/rotate — same contract, plus previousId */
  rotateWebhook: (id: string) => Promise<RegisteredWebhook>;
}

const msg = (e: unknown): string => (e instanceof Error ? e.message : String(e));

export function useConsoleData(): ConsoleData {
  const [service, setService] = useState<Service | null>(null);
  const [webhooks, setWebhooks] = useState<WebhooksResponse | null>(null);
  const [dials, setDialsState] = useState<Dials | null>(null);
  const [intent, setIntent] = useState<Record<string, Record<string, boolean>> | null>(null);
  const [monitor, setMonitor] = useState<MonitorResponse | null>(null);
  const [tts, setTts] = useState<TtsRow[]>([]);
  const [calendly, setCalendly] = useState<CalendlyRow[]>([]);
  const [secrets, setSecrets] = useState<SecretStatus[]>([]);
  const [dialChanges, setDialChanges] = useState<Record<string, DialChange | null>>({});
  const [actions, setActions] = useState<ActionRow[]>([]);
  const [health, setHealth] = useState<Health | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // Full load.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const results = await Promise.allSettled([
        authFetch<Service>('/api/service'),
        authFetch<WebhooksResponse>('/api/webhooks'),
        authFetch<DialsIntent>('/api/dials/intent'),
        authFetch<MonitorResponse>('/api/monitor'),
        authFetch<TtsRow[]>(`/api/ledger/tts?limit=${LEDGER_LIMIT}`),
        authFetch<CalendlyRow[]>(`/api/ledger/calendly?limit=${LEDGER_LIMIT}`),
        authFetch<SecretStatus[]>('/api/secrets/status'),
        authFetch<Health>('/healthz'),
        authFetch<ActionRow[]>('/api/actions?limit=30'),
      ]);
      if (cancelled) return;
      const [svc, whs, di, mon, tl, cl, sec, hz, ac] = results;
      if (svc.status === 'fulfilled') setService(svc.value);
      if (whs.status === 'fulfilled') setWebhooks(whs.value);
      if (di.status === 'fulfilled') {
        setDialsState(di.value.dials);
        setIntent(di.value.intent);
        setDialChanges(di.value.changes ?? {});
      }
      if (mon.status === 'fulfilled') setMonitor(mon.value);
      if (tl.status === 'fulfilled') setTts(Array.isArray(tl.value) ? tl.value : []);
      if (cl.status === 'fulfilled') setCalendly(Array.isArray(cl.value) ? cl.value : []);
      if (sec.status === 'fulfilled') setSecrets(Array.isArray(sec.value) ? sec.value : []);
      if (hz.status === 'fulfilled') setHealth(hz.value);
      if (ac.status === 'fulfilled') setActions(Array.isArray(ac.value) ? ac.value : []);
      const failed = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[];
      setError(failed.length ? failed.map((f) => msg(f.reason)).join(' · ') : null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  // Poll the live bits while visible.
  useEffect(() => {
    const tick = async (): Promise<void> => {
      if (document.visibilityState !== 'visible') return;
      try {
        const [mon, dl] = await Promise.all([
          authFetch<MonitorResponse>('/api/monitor'),
          authFetch<Dials>('/api/dials'),
        ]);
        if (!alive.current) return;
        setMonitor(mon);
        setDialsState(dl);
      } catch {
        /* a failed poll keeps the last good view; the next tick retries */
      }
    };
    const onVisible = (): void => void tick();
    const id = window.setInterval(onVisible, POLL_MS);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  // After any write: the action trail and dial who/when are cheap to re-read and always worth being current.
  const refreshMeta = useCallback(async () => {
    const [di, ac] = await Promise.allSettled([
      authFetch<DialsIntent>('/api/dials/intent'),
      authFetch<ActionRow[]>('/api/actions?limit=30'),
    ]);
    if (!alive.current) return;
    if (di.status === 'fulfilled') setDialChanges(di.value.changes ?? {});
    if (ac.status === 'fulfilled') setActions(Array.isArray(ac.value) ? ac.value : []);
  }, []);

  const setDials = useCallback(async (patch: DialPatch) => {
    const next = await authFetch<Dials>('/api/dials', {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
    setDialsState(next);
    void refreshMeta();
  }, [refreshMeta]);

  const replayTts = useCallback(async (key: string, dryRun: boolean) => {
    const res = await authFetch<ReplayResponse>(`/api/ledger/tts/${encodeURIComponent(key)}/replay`, {
      method: 'POST',
      body: JSON.stringify({ dryRun }),
    });
    setTts((rows) => rows.map((r) => (r.submission_key === key ? res.row : r)));
    void refreshMeta();
    return res;
  }, [refreshMeta]);

  const sendProbe = useCallback(async (req: ProbeRequest) => {
    const res = await authFetch<ProbeResult>('/api/tools/probe', { method: 'POST', body: JSON.stringify(req) });
    void refreshMeta();
    return res;
  }, [refreshMeta]);

  const refreshWebhooks = useCallback(async () => {
    const [whs, sec] = await Promise.allSettled([
      authFetch<WebhooksResponse>('/api/webhooks'),
      authFetch<SecretStatus[]>('/api/secrets/status'),
    ]);
    if (!alive.current) return;
    if (whs.status === 'fulfilled') setWebhooks(whs.value);
    if (sec.status === 'fulfilled') setSecrets(Array.isArray(sec.value) ? sec.value : []);
  }, []);

  const registerWebhook = useCallback(async (pipeline: PipelineKey) => {
    const res = await authFetch<RegisteredWebhook>('/api/webhooks', {
      method: 'POST',
      body: JSON.stringify({ pipeline, confirm: 'REGISTER' }),
    });
    void refreshWebhooks();
    void refreshMeta();
    return res;
  }, [refreshMeta, refreshWebhooks]);

  const rotateWebhook = useCallback(async (id: string) => {
    const res = await authFetch<RegisteredWebhook>(`/api/webhooks/${encodeURIComponent(id)}/rotate`, {
      method: 'POST',
      body: JSON.stringify({ confirm: id }),
    });
    void refreshWebhooks();
    void refreshMeta();
    return res;
  }, [refreshMeta, refreshWebhooks]);

  const deleteWebhook = useCallback(async (id: string, confirm: string) => {
    const res = await authFetch<{ deleted: string; webhooks: WebhooksResponse['webhooks'] }>(
      `/api/webhooks/${encodeURIComponent(id)}`,
      { method: 'DELETE', body: JSON.stringify({ confirm }) },
    );
    setWebhooks((w) => (w ? { ...w, webhooks: res.webhooks } : w));
    void refreshMeta();
  }, [refreshMeta]);

  return {
    service,
    webhooks,
    dials,
    intent,
    dialChanges,
    monitor,
    tts,
    calendly,
    secrets,
    actions,
    health,
    loading,
    error,
    reload,
    setDials,
    replayTts,
    sendProbe,
    deleteWebhook,
    registerWebhook,
    rotateWebhook,
  };
}
